import React, {useRef, useEffect} from 'react'
import styled from 'styled-components'
import Label from './Label'

const SliderContainer = styled.span`
  transition: left 0.3s ease-out;
`
const StyledSlider = styled.div`
  appearance: none;
  border-radius: 100%;
  cursor: pointer;
  display: block;
  margin-left: -13px;
  margin-top: -18px;
  outline: none;
  position: absolute;
  top: 50%;
  transition: transform 0.3s ease-out, box-shadow 0.3s ease-out;
  width: 26px;
  height: 26px;
  background: #ffffff;
  box-shadow: 0 2px 5px 0 #a4a9b7;

  &:active {
    transform: scale(1.3);
  }

  &:focus {
    box-shadow: 0 0 0 5px transparentize(#3f51b5, 0.8);
  }

  .input-range--disabled & {
    background: #cccccc;
    border: 1px solid #cccccc;
    box-shadow: none;
    transform: none;
  }
`
const Slider = props => {
  let node = useRef(null)

  const {
    onSliderDrag,
    onSliderKeyDown,
    type,
    formatLabel,
    value,
    percentage,
    showSliderLabel
  } = props

  useEffect(() => {
    return () => {
      removeDocumentMouseMoveListener()
      removeDocumentMouseUpListener()
      removeDocumentTouchEndListener()
      removeDocumentTouchMoveListener()
    }
  }, [])
  const getStyle = () => {
    const perc = (percentage || 0) * 100
    const style = {
      position: 'absolute',
      left: `${perc}%`
    }

    return style
  }

  /**
   * Listen to mousemove event
   * @private
   * @return {void}
   */
  const addDocumentMouseMoveListener = () => {
    removeDocumentMouseMoveListener()
    node.current.ownerDocument.addEventListener('mousemove', handleMouseMove)
  }

  /**
   * Listen to mouseup event
   * @private
   * @return {void}
   */
  const addDocumentMouseUpListener = () => {
    removeDocumentMouseUpListener()
    node.current.ownerDocument.addEventListener('mouseup', handleMouseUp)
  }

  /**
   * Listen to touchmove event
   * @private
   * @return {void}
   */
  const addDocumentTouchMoveListener = () => {
    removeDocumentTouchMoveListener()
    node.current.ownerDocument.addEventListener('touchmove', handleTouchMove)
  }

  /**
   * Listen to touchend event
   * @private
   * @return {void}
   */
  const addDocumentTouchEndListener = () => {
    removeDocumentTouchEndListener()
    node.current.ownerDocument.addEventListener('touchend', handleTouchEnd)
  }

  /**
   * @private
   * @return {void}
   */
  const removeDocumentMouseMoveListener = () => {
    node.current.ownerDocument.removeEventListener('mousemove', handleMouseMove)
  }

  /**
   * @private
   * @return {void}
   */
  const removeDocumentMouseUpListener = () => {
    node.current.ownerDocument.removeEventListener('mouseup', handleMouseUp)
  }

  /**
   * @private
   * @return {void}
   */
  const removeDocumentTouchMoveListener = () => {
    node.current.ownerDocument.removeEventListener('touchmove', handleTouchMove)
  }

  /**
   * @private
   * @return {void}
   */
  const removeDocumentTouchEndListener = () => {
    node.current.ownerDocument.removeEventListener('touchend', handleTouchEnd)
  }

  const handleKeyDown = evt => {
    onSliderKeyDown(evt, type)
  }

  const handleTouchEnd = () => {
    removeDocumentTouchMoveListener()
    removeDocumentTouchEndListener()
  }

  const handleTouchStart = () => {
    addDocumentTouchEndListener()
    addDocumentTouchMoveListener()
  }

  const handleMouseDown = evt => {
    addDocumentMouseMoveListener()
    addDocumentMouseUpListener()
  }

  const handleMouseMove = evt => {
    onSliderDrag(evt, type)
  }

  const handleTouchMove = evt => {
    onSliderDrag(evt, type)
  }

  const handleMouseUp = () => {
    removeDocumentMouseMoveListener()
    removeDocumentMouseUpListener()
  }

  const style = getStyle()

  return (
    <SliderContainer ref={node} style={style}>
      {showSliderLabel && (
        <Label
          formatLabel={formatLabel}
          type="value"
          showSliderLabel={showSliderLabel}
        >
          {value}
        </Label>
      )}
      <StyledSlider
        draggable="false"
        onKeyDown={handleKeyDown}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        role="slider"
        tabIndex="0"
        data-testid="input-range-slider"
      />
    </SliderContainer>
  )
}

export default Slider
